import { obtenerAsientosPorEmpresa } from "./asientosService";
import { obtenerComprasPorEmpresa, obtenerCuentasPagarPorEmpresa, obtenerPagosPorEmpresa } from "./comprasService";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const inPeriodo = (fecha, periodo) => {
  if (!periodo) return true;
  return String(fecha || "").slice(0, 7) === String(periodo);
};

export const obtenerReporteFinanciero = async (rucEmpresa, periodo = "") => {
  const [asientos, compras, cxp, pagos] = await Promise.all([
    obtenerAsientosPorEmpresa(rucEmpresa),
    obtenerComprasPorEmpresa(rucEmpresa),
    obtenerCuentasPagarPorEmpresa(rucEmpresa),
    obtenerPagosPorEmpresa(rucEmpresa),
  ]);

  const sumByClass = (lineas, side = "debe") =>
    lineas.reduce((acc, x) => {
      const clase = String(x.cuenta || "")[0] || "0";
      const monto = toNum(x.monto);
      acc[clase] = (acc[clase] || 0) + monto;
      return acc;
    }, {});

  const debeByClass = {};
  const haberByClass = {};
  const asientosPeriodo = asientos.filter((a) => inPeriodo(a.fecha, periodo));
  asientosPeriodo.forEach((a) => {
    const d = sumByClass(a.debe || [], "debe");
    const h = sumByClass(a.haber || [], "haber");
    Object.keys(d).forEach((k) => {
      debeByClass[k] = (debeByClass[k] || 0) + d[k];
    });
    Object.keys(h).forEach((k) => {
      haberByClass[k] = (haberByClass[k] || 0) + h[k];
    });
  });

  const ingresos = Math.max(0, (haberByClass["7"] || 0) - (debeByClass["7"] || 0));
  const gastos = Math.max(0, (debeByClass["6"] || 0) - (haberByClass["6"] || 0));
  const utilidad = ingresos - gastos;

  const activos = Math.max(0, (debeByClass["1"] || 0) - (haberByClass["1"] || 0));
  const pasivos = Math.max(0, (haberByClass["4"] || 0) - (debeByClass["4"] || 0));
  const patrimonio = activos - pasivos;

  const comprasAprobadas = compras.filter((c) => c.estados?.documento === "APROBADO" && inPeriodo(c.comprobante?.fecha_emision, periodo));
  const pagosAprobados = pagos.filter((p) => p.estado_aprobacion === "APROBADO" && inPeriodo(p.fecha_pago, periodo));

  const comprasTotal = comprasAprobadas.reduce((acc, c) => acc + toNum(c.montos?.total), 0);
  const pagosTotal = pagosAprobados.reduce((acc, p) => acc + toNum(p.monto), 0);
  const pendientePagar = cxp.reduce((acc, p) => acc + toNum(p.saldo_pendiente), 0);
  const flujoNeto = -pagosTotal;
  const totalDevengado = comprasAprobadas.reduce((acc, c) => acc + toNum(c.montos?.total), 0);
  const totalCaja = pagosAprobados.reduce((acc, p) => acc + toNum(p.monto), 0);
  const devengadoVsCaja = Number((totalDevengado - totalCaja).toFixed(2));

  const byCentroCosto = comprasAprobadas.reduce((acc, c) => {
    const centro = c.clasificacion?.centro_costo || "SIN_CENTRO";
    acc[centro] = (acc[centro] || 0) + toNum(c.montos?.total);
    return acc;
  }, {});

  return {
    periodo: periodo || "GENERAL",
    pnl: { ingresos, gastos, utilidad },
    balance: { activos, pasivos, patrimonio },
    cashflow: { salidas: pagosTotal, entradas: 0, neto: flujoNeto, pendientePagar },
    devengadoVsCaja: { devengado: totalDevengado, caja: totalCaja, diferencia: devengadoVsCaja },
    centrosCosto: Object.entries(byCentroCosto)
      .map(([centro, total]) => ({ centro, total: Number(toNum(total).toFixed(2)) }))
      .sort((a, b) => b.total - a.total),
    compras: { total: comprasTotal, cantidad: comprasAprobadas.length },
    asientos: { total: asientosPeriodo.length, debe: Object.values(debeByClass).reduce((a, b) => a + b, 0), haber: Object.values(haberByClass).reduce((a, b) => a + b, 0) },
  };
};

export const obtenerReporteFinancieroComparativo = async ({ rucEmpresa, periodoActual, periodoAnterior }) => {
  const [actual, anterior] = await Promise.all([
    obtenerReporteFinanciero(rucEmpresa, periodoActual),
    obtenerReporteFinanciero(rucEmpresa, periodoAnterior),
  ]);

  const delta = (a, b) => Number((toNum(a) - toNum(b)).toFixed(2));
  return {
    actual,
    anterior,
    variacion: {
      ingresos: delta(actual.pnl?.ingresos, anterior.pnl?.ingresos),
      gastos: delta(actual.pnl?.gastos, anterior.pnl?.gastos),
      utilidad: delta(actual.pnl?.utilidad, anterior.pnl?.utilidad),
      flujoNeto: delta(actual.cashflow?.neto, anterior.cashflow?.neto),
      compras: delta(actual.compras?.total, anterior.compras?.total),
    },
  };
};
